/**
 * Makes a cloned floating tree size to its content so `flip` / `size` see
 * the unconstrained height. WebKit collapses `height: 100%` / `max-height: 100%`
 * (stylesheet, not just inline) when the parent is `height: max-content`.
 *
 * Does not smash descendant `height` to `auto` — empty visuals (and anything
 * else with a specified px/rem/calc height) have absolutely positioned cards
 * that would then collapse to 0 and lie to `flip`. Percentage heights that
 * do collapse are restored after layout via `expandCollapsedFloatingCloneHeights`.
 *
 * Leaves `min-height` alone so reserved floors (e.g. two file rows) survive.
 *
 * `keepContentMaxHeight` leaves the descendants' own max-height in place, so
 * the measurement saturates at the height the panel will really take instead
 * of at its unbounded content. The root clamp always goes: that one is last
 * frame's `size()` output, and the dropdown vars go with it so the content's
 * max-height resolves against its own fallback.
 */
export function relaxFloatingCloneSizeConstraints(
  root: HTMLElement,
  keepContentMaxHeight = false,
): void {
  root.style.maxHeight = 'none'
  root.style.maxWidth = 'none'
  root.style.height = 'max-content'
  root.style.width = 'max-content'
  root.style.minHeight = 'auto'
  root.style.minWidth = '0'
  root.style.overflow = 'visible'
  root.style.removeProperty('--dropdown-available-height')
  root.style.removeProperty('--dropdown-available-width')

  for (const node of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
    if (!keepContentMaxHeight) {
      node.style.maxHeight = 'none'
    }
    node.style.maxWidth = 'none'
    node.style.overflow = 'visible'
    node.style.removeProperty('--dropdown-available-height')
    node.style.removeProperty('--dropdown-available-width')
  }
}

/**
 * After the clone is in the document, turn percentage/`100%` descendants that
 * collapsed to 0 (WebKit + `max-content` parent) into `height: auto` so they
 * hug content. Skips nodes that already have a real used height (empty visual).
 */
export function expandCollapsedFloatingCloneHeights(
  cloneRoot: HTMLElement,
  liveRoot: HTMLElement,
): void {
  const liveNodes = [liveRoot, ...Array.from(liveRoot.querySelectorAll<HTMLElement>('*'))]
  const cloneNodes = [cloneRoot, ...Array.from(cloneRoot.querySelectorAll<HTMLElement>('*'))]
  const count = Math.min(liveNodes.length, cloneNodes.length)

  for (let index = 1; index < count; index++) {
    if (cloneNodes[index].offsetHeight < 1 && liveNodes[index].offsetHeight > 1) {
      cloneNodes[index].style.height = 'auto'
    }
  }
}
